import React, { useState, useMemo, useCallback, useEffect, useContext, useRef } from 'react';
import { FaSyncAlt, FaTable, FaThLarge, FaBroadcastTower, FaFilter, FaNetworkWired } from 'react-icons/fa';
import { useImmer } from 'use-immer';
import { useSelector } from 'react-redux';
import './voip-dashboard.scss';

// MUI Components
import {
  Autocomplete,
  TextField,
  Chip,
  Button,
  Typography,
  Tabs,
  Tab,
  Box,
} from '@mui/material';

// Components
import VoIPStatCards from './components/VoIPStatCards';
import VoIPCharts from './components/VoIPCharts';
import VoIPAlertPanel from './components/VoIPAlertPanel';
import VoIPDeviceTable from './components/VoIPDeviceTable';
import VoIPDeviceCards from './components/VoIPDeviceCards';
import VoIPDetailModal from './components/VoIPDetailModal';
import { AutocompleteStationListPop } from '../AutocompleteStationListPop/AutocompleteStationListPop';

// Types and Constants
import { VoIPDevice, VoIPAlert, ViewMode } from './utils/voipTypes';
import {
  calculateStats,
  calculateStationStats,
} from './utils/voipConstants';

// API Service
import {
  fetchVoIPTelemetry,
  fetchVoIPDeviceTypes,
  generateAlertsFromDevices,
} from './utils/voipApiService';

// Context and Utils
import { ActiveAlarmsData } from '../Alarms/ActiveAlarms';
import { toast } from 'react-hot-toast';
import { getParsedLocalStorage } from '../../Utils/storage-utils';
import { extractParsedDataFromPermissions } from '../../Utils/Utils';
import { selectedCustomersSelector } from '../../features/authentication/auth-slice';

// Filter interface
interface FilterState {
  zone_ids: any[];
  div_ids: any[];
  customer_ids: any[];
}

const initialFilters: FilterState = {
  zone_ids: [],
  div_ids: [],
  customer_ids: [],
};

const VoIPDashboard: React.FC = () => {
  // State
  const [activeView, setActiveView] = useState<ViewMode>('table');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState<VoIPDevice | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Data state
  const [devices, setDevices] = useState<VoIPDevice[]>([]);
  const [deviceTypes, setDeviceTypes] = useState<any[]>([]);
  const [selectedDeviceTypeIndex, setSelectedDeviceTypeIndex] = useState<number>(0);

  // Filter state
  const [filters, setFilters] = useImmer<FilterState>(initialFilters);

  // Refs
  const apiControllerRef = useRef<AbortController | null>(null);

  // Context and Redux
  const { customerIdList }: any = useContext(ActiveAlarmsData);
  const selectedCustomersList = useSelector(selectedCustomersSelector);

  // Parse permissions for Zone/Division/Station dropdowns
  const user_permissions = getParsedLocalStorage('permissions', null);
  const parsedPermissions = useMemo(() => {
    return extractParsedDataFromPermissions(user_permissions);
  }, [user_permissions]);

  // Permitted customer IDs for filtering
  const permittedCustomerIds = useMemo(() => {
    return selectedCustomersList?.map((c: any) => ({ id: c.customerId, value: c.customerName })) || [];
  }, [selectedCustomersList]);

  // Get VoIP system ID from systems list
  const voipSystemId = useMemo(() => {
    try {
      const systemsList = getParsedLocalStorage('systems_list', []);
      const voipSystem = systemsList.find((sys: any) =>
        sys.code?.toUpperCase() === 'VOIP' ||
        sys.name?.toUpperCase().includes('VOIP')
      );
      return voipSystem?.id || null;
    } catch {
      return null;
    }
  }, []);

  // Zone dropdown options
  const zoneOptions = useMemo(() => {
    return parsedPermissions.zones || [];
  }, [parsedPermissions.zones]);

  // Division dropdown options - filtered by selected zones
  const divisionOptions = useMemo(() => {
    let totalDivs = parsedPermissions.divisions || [];
    if (filters.zone_ids.length > 0) {
      const selectedZoneIds = filters.zone_ids.map((z: any) => z.id);
      totalDivs = totalDivs.filter((div: any) => selectedZoneIds.includes(div.zone?.id));
    }
    return totalDivs;
  }, [parsedPermissions.divisions, filters.zone_ids]);

  // Station dropdown options - filtered by selected zones and divisions
  const stationOptions = useMemo(() => {
    let filteredStations = (parsedPermissions.stations || []).filter((stn: any) =>
      permittedCustomerIds.find((c: any) => c.id === stn.id)
    );

    if (filters.div_ids.length > 0) {
      const selectedDivIds = filters.div_ids.map((d: any) => d.id);
      filteredStations = filteredStations.filter((stn: any) => selectedDivIds.includes(stn.division?.id));
    } else if (filters.zone_ids.length > 0) {
      const selectedZoneIds = filters.zone_ids.map((z: any) => z.id);
      filteredStations = filteredStations.filter((stn: any) => selectedZoneIds.includes(stn.zone?.id));
    }

    return filteredStations;
  }, [parsedPermissions.stations, permittedCustomerIds, filters.div_ids, filters.zone_ids]);

  // Create a mapping of customer ID to station name from selectedCustomersList
  // This mapping uses customerId from the telemetry API to get the correct station name
  const customerIdToStationName = useMemo(() => {
    const mapping: Record<string, string> = {};
    // Primary source: selectedCustomersList has customerId and customerName
    (selectedCustomersList || []).forEach((customer: any) => {
      if (customer.customerId && customer.customerName) {
        mapping[customer.customerId] = customer.customerName;
        // Also map string version of customerId
        mapping[String(customer.customerId)] = customer.customerName;
      }
    });
    // Secondary source: parsedPermissions.stations (id and value)
    (parsedPermissions.stations || []).forEach((stn: any) => {
      if (stn.id && stn.value && !mapping[stn.id]) {
        mapping[stn.id] = stn.value;
        mapping[String(stn.id)] = stn.value;
      }
    });
    return mapping;
  }, [selectedCustomersList, parsedPermissions.stations]);

  // Get customer IDs for API call based on filters
  const getFilteredCustomerIds = useCallback(() => {
    if (filters.customer_ids.length > 0) {
      return filters.customer_ids.map((c: any) => c.id);
    }
    if (filters.div_ids.length > 0 || filters.zone_ids.length > 0) {
      return stationOptions.map((s: any) => s.id);
    }
    return customerIdList;
  }, [filters.customer_ids, filters.div_ids, filters.zone_ids, stationOptions, customerIdList]);

  // Get selected device type
  const selectedDeviceType = useMemo(() => {
    return deviceTypes[selectedDeviceTypeIndex]?.type || null;
  }, [selectedDeviceTypeIndex, deviceTypes]);

  // Load device types
  const loadDeviceTypes = useCallback(async () => {
    if (!voipSystemId || customerIdList.length === 0) return;

    try {
      const types = await fetchVoIPDeviceTypes(voipSystemId, customerIdList);
      setDeviceTypes(types);
    } catch (err) {
      console.error('Error loading device types:', err);
    }
  }, [voipSystemId, customerIdList]);

  // Load telemetry data
  const loadTelemetryData = useCallback(async (showLoading = true) => {
    const filteredCustomerIds = getFilteredCustomerIds();

    if (!voipSystemId || filteredCustomerIds.length === 0) {
      setError('No VoIP system configured or no stations assigned');
      setIsLoading(false);
      return;
    }

    // Cancel previous request
    if (apiControllerRef.current) {
      apiControllerRef.current.abort();
    }
    apiControllerRef.current = new AbortController();

    if (showLoading) {
      setIsLoading(true);
    } else {
      setIsRefreshing(true);
    }
    setError(null);

    try {
      const voipDevices = await fetchVoIPTelemetry(
        voipSystemId,
        filteredCustomerIds,
        selectedDeviceType || undefined
      );

      // Override station names using the customer ID to station name mapping
      // The mapping contains customerId -> customerName (full station name)
      const devicesWithMappedStations = voipDevices.map((device) => {
        const customerId = device.customerId;
        // Skip if no customerId
        if (!customerId) {
          return device;
        }
        // Try both string and original format for customerId lookup
        const mappedStationName = customerIdToStationName[customerId] ||
                                   customerIdToStationName[String(customerId)];

        if (mappedStationName) {
          return {
            ...device,
            station: mappedStationName,
          };
        }
        return device;
      });

      console.log('VoIP Devices with station mapping:', {
        totalDevices: devicesWithMappedStations.length,
        customerIdToStationName,
        sampleDevice: devicesWithMappedStations[0],
      });

      setDevices(devicesWithMappedStations);
    } catch (err: any) {
      if (err.name !== 'AbortError') {
        console.error('Error loading telemetry:', err);
        setError(err.message || 'Failed to load VoIP data');
        toast.error('Failed to load VoIP telemetry data');
      }
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [voipSystemId, getFilteredCustomerIds, selectedDeviceType, customerIdToStationName]);

  // Initial data load
  useEffect(() => {
    if (customerIdList.length > 0) {
      loadDeviceTypes();
    }
  }, [customerIdList, voipSystemId]);

  // Load data when device types are loaded or device type changes
  useEffect(() => {
    if (customerIdList.length > 0 && voipSystemId) {
      loadTelemetryData();
    }
  }, [customerIdList, voipSystemId, selectedDeviceType]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      if (!isLoading && !isRefreshing) {
        loadTelemetryData(false);
      }
    }, 30000);

    return () => {
      clearInterval(interval);
    };
  }, [loadTelemetryData, isLoading, isRefreshing]);

  // Get unique stations from devices for grouping
  const deviceStations = useMemo(() => {
    const uniqueStations = [...new Set(devices.map(d => d.station))];
    return uniqueStations.sort();
  }, [devices]);

  // Generate alerts from devices (based on ICMP ping status)
  const alerts = useMemo(() => generateAlertsFromDevices(devices), [devices]);

  // Calculate stats
  const stats = useMemo(() => calculateStats(devices), [devices]);

  // Calculate station stats
  const stationStats = useMemo(() => calculateStationStats(devices), [devices]);

  // Handle device type tab change
  const handleDeviceTypeChange = useCallback((event: React.SyntheticEvent, newValue: number) => {
    setSelectedDeviceTypeIndex(newValue);
  }, []);

  // Filter handlers
  const handleFilterChange = useCallback((value: any, field: keyof FilterState) => {
    setFilters((prev) => {
      (prev as any)[field] = value;
      // Clear dependent filters
      if (field === 'zone_ids') {
        prev.div_ids = [];
        prev.customer_ids = [];
      } else if (field === 'div_ids') {
        prev.customer_ids = [];
      }
    });
  }, [setFilters]);

  const handleApplyFilters = useCallback(() => {
    loadTelemetryData();
  }, [loadTelemetryData]);

  const handleClearFilters = useCallback(() => {
    setFilters(initialFilters);
    setTimeout(() => {
      loadTelemetryData();
    }, 100);
  }, [setFilters, loadTelemetryData]);

  const handleRefresh = useCallback(() => {
    loadTelemetryData(false);
  }, [loadTelemetryData]);

  const handleDeviceClick = useCallback((device: VoIPDevice) => {
    setSelectedDevice(device);
    setIsModalOpen(true);
  }, []);

  const handleAlertClick = useCallback((alert: VoIPAlert) => {
    const device = devices.find(d => d.id === alert.deviceId);
    if (device) {
      setSelectedDevice(device);
      setIsModalOpen(true);
    }
  }, [devices]);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedDevice(null);
  }, []);

  // Render tag chips for autocomplete
  const renderTags = (value: any[], getTagProps: any) => {
    if (value.length === 0) return null;
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <Chip size="small" variant="outlined" label={value[0]?.value} {...getTagProps(0)} />
        {value.length > 1 && (
          <AutocompleteStationListPop title={value.map((opt) => opt?.value ?? '').join(', ')}>
            <Chip size="small" label={`+${value.length - 1}`} />
          </AutocompleteStationListPop>
        )}
      </div>
    );
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="voip-dashboard">
        <header className="voip-header">
          <div className="logo-container">
            <div className="voip-logo"><FaNetworkWired /></div>
            <div className="voip-title">
              <h1>VoIP Network Monitoring</h1>
              <div className="voip-subtitle">ICMP Ping-based device health monitoring</div>
            </div>
          </div>
          <div className="system-status">
            <div className="status-indicator">
              <div className="status-dot loading" />
              <span>Loading...</span>
            </div>
          </div>
        </header>
        <div className="loading-container">
          <div className="loading-spinner">
            <FaBroadcastTower className="spin" />
          </div>
          <p>Loading VoIP device data...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error && devices.length === 0) {
    return (
      <div className="voip-dashboard">
        <header className="voip-header">
          <div className="logo-container">
            <div className="voip-logo"><FaNetworkWired /></div>
            <div className="voip-title">
              <h1>VoIP Network Monitoring</h1>
              <div className="voip-subtitle">ICMP Ping-based device health monitoring</div>
            </div>
          </div>
        </header>
        <div className="error-container">
          <div className="error-message">
            <p>{error}</p>
            <button className="retry-btn" onClick={() => loadTelemetryData()}>
              <FaSyncAlt /> Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="voip-dashboard">
      {/* Header */}
      <header className="voip-header">
        <div className="logo-container">
          <div className="voip-logo"><FaNetworkWired /></div>
          <div className="voip-title">
            <h1>VoIP Network Monitoring</h1>
            <div className="voip-subtitle">ICMP Ping-based device health monitoring</div>
          </div>
        </div>
        <div className="system-status">
          <div className="status-indicator">
            <div className={`status-dot ${isRefreshing ? 'loading' : 'online'}`} />
            <span>{isRefreshing ? 'Refreshing...' : 'Operational'}</span>
          </div>
        </div>
      </header>

      {/* Device Type Tabs */}
      {deviceTypes.length > 0 && (
        <Box className="device-type-tabs">
          <Tabs
            value={selectedDeviceTypeIndex}
            onChange={handleDeviceTypeChange}
            variant="scrollable"
            scrollButtons="auto"
            aria-label="Device type tabs"
          >
            {deviceTypes.map((type, index) => (
              <Tab key={type.type || index} label={type.label || type.type} />
            ))}
          </Tabs>
        </Box>
      )}

      {/* Filter Section */}
      <div className="voip-filter-section">
        <div className="filter-row">
          {/* Zone Dropdown */}
          <Autocomplete
            size="small"
            className="filter-input"
            multiple
            disableCloseOnSelect
            options={zoneOptions}
            value={filters.zone_ids}
            onChange={(_, newValue) => handleFilterChange(newValue, 'zone_ids')}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            getOptionLabel={(opt) => opt.value || ''}
            renderInput={(params) => <TextField {...params} label="Zones" />}
            renderTags={renderTags}
          />

          {/* Division Dropdown */}
          <Autocomplete
            size="small"
            className="filter-input"
            multiple
            disableCloseOnSelect
            options={divisionOptions}
            value={filters.div_ids}
            onChange={(_, newValue) => handleFilterChange(newValue, 'div_ids')}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            getOptionLabel={(opt) => opt.value || ''}
            renderInput={(params) => <TextField {...params} label="Divisions" />}
            renderTags={renderTags}
          />

          {/* Station Dropdown */}
          <Autocomplete
            size="small"
            className="filter-input"
            multiple
            disableCloseOnSelect
            options={stationOptions}
            value={filters.customer_ids}
            onChange={(_, newValue) => handleFilterChange(newValue, 'customer_ids')}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            getOptionLabel={(opt) => opt.value || ''}
            renderInput={(params) => <TextField {...params} label="Stations" />}
            renderTags={renderTags}
          />

          {/* Filter Buttons */}
          <div className="filter-actions">
            <Button
              variant="contained"
              size="small"
              onClick={handleApplyFilters}
              disabled={isRefreshing}
              startIcon={<FaFilter />}
            >
              Apply
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={handleClearFilters}
              disabled={isRefreshing}
            >
              Clear
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={handleRefresh}
              disabled={isRefreshing}
              startIcon={<FaSyncAlt className={isRefreshing ? 'spin' : ''} />}
            >
              Refresh
            </Button>
          </div>
        </div>

        {/* Summary Info */}
        <div className="filter-summary">
          <Typography variant="body2" color="textSecondary">
            Showing <strong>{devices.length}</strong> devices across <strong>{deviceStations.length}</strong> stations
            {filters.zone_ids.length > 0 && ` in ${filters.zone_ids.length} zone(s)`}
            {filters.div_ids.length > 0 && `, ${filters.div_ids.length} division(s)`}
            {filters.customer_ids.length > 0 && `, ${filters.customer_ids.length} station(s)`}
          </Typography>
        </div>
      </div>

      {/* Stats Cards */}
      <VoIPStatCards stats={stats} />

      {/* Charts Section - Unreachable Devices Focus */}
      <div className="charts-section">
        <VoIPCharts stationStats={stationStats} devices={devices} />
      </div>

      {/* Alerts Section */}
      {alerts.length > 0 && (
        <div className="alerts-section">
          <VoIPAlertPanel alerts={alerts} onAlertClick={handleAlertClick} />
        </div>
      )}

      {/* Device View Section */}
      <div className="tabs-section">
        <div className="tabs-header">
          <button
            className={`tab-button ${activeView === 'table' ? 'active' : ''}`}
            onClick={() => setActiveView('table')}
          >
            <FaTable />
            Table View
          </button>
          <button
            className={`tab-button ${activeView === 'card' ? 'active' : ''}`}
            onClick={() => setActiveView('card')}
          >
            <FaThLarge />
            Card View
          </button>
        </div>

        {/* Table View */}
        <div className={`tab-content ${activeView === 'table' ? 'active' : ''}`}>
          <VoIPDeviceTable devices={devices} onDeviceClick={handleDeviceClick} />
        </div>

        {/* Card View */}
        <div className={`tab-content ${activeView === 'card' ? 'active' : ''}`}>
          <VoIPDeviceCards devices={devices} onDeviceClick={handleDeviceClick} />
        </div>
      </div>

      {/* Device Detail Modal */}
      <VoIPDetailModal
        isOpen={isModalOpen}
        device={selectedDevice}
        onClose={handleCloseModal}
      />
    </div>
  );
};

export default VoIPDashboard;
